import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  UncontrolledTooltip,
  Pagination,
  PaginationItem,
  PaginationLink,
  FormGroup,
  Label,
  Button,
} from 'reactstrap';

import PageTitle from '../../components/Common/PageTitle';
import { ShowNotification, ObjectHasValues } from '../../helpers/general/generalHelpers';
import { getPaginationInformation, initialPaginationObject } from '../../helpers/general/pagination';
import { deleteCaseStudies, getCaseStudies } from './services/CaseStudies';
import Loader from '../../shared/loader/loader';
import AvForm from 'availity-reactstrap-validation/lib/AvForm';
import AvField from 'availity-reactstrap-validation/lib/AvField';
import ModalAddEditCaseStudy from './ModalAddEditCaseStudy';
import { getSubjects } from '../Subjects/Services/subject-service';

const CaseStudies = (props) => {
  const [pagination, setPagination] = useState(initialPaginationObject);
  const [currentPage, setCurrentPage] = useState(1);
  const filterForm = useRef(null);
  const [filters, setFilters] = useState(null);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loader, setLoader] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  // Show notification if present
  if (props.location.notification && props.location.notification.show) {
    const notification = props.location.notification;
    ShowNotification({
      type: 'success',
      message: notification.message,
    });
  }

  // Fetch subjects
  useMemo(() => {
    setSubjectsLoading(true);
    getSubjects(undefined, undefined, 100)
      .then((response) => {
        const subjList = response.data.subjects.map((subj) => ({
          ...subj,
          id: subj._id,
          value: subj.title,
          label: subj.title,
        }));
        setSubjects(subjList);
      })
      .catch((error) => {
        console.error('Error fetching subjects:', error);
      })
      .finally(() => {
        setSubjectsLoading(false);
      });
  }, []);

  // Fetch case studies
  useEffect(() => {
    setLoader(true);
    getCaseStudies(currentPage, filters)
      .then((response) => {
        const caseStudies = response.data.caseStudies;
        setCaseStudies(caseStudies);
        setPagination(getPaginationInformation(response.data.Count, currentPage));
      })
      .catch((error) => {
        console.error('Error fetching case studies:', error);
      })
      .finally(() => {
        setLoader(false);
      });
  }, [currentPage, filters]);

  // Pagination handler
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Filter handler
  const handleFilters = (event, values) => {
    if (ObjectHasValues(values)) {
      setFilters(values);
    }
  };

  // Clear filters
  const clearFilters = () => {
    filterForm.current.reset();
    setFilters(undefined);
  };

  // Toggle modal
  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleDeleteCaseStudy = (caseStudy) =>{
    setLoader(true);
    deleteCaseStudies(caseStudy._id)
      .then((response) => {
        const message = 'Case study deleted successfully';
      ShowNotification({ type: 'error', message });
      window.location.reload()
      })
      .catch((error) => {
        console.error('Error fetching case studies:', error);
      })
      .finally(() => {
        setLoader(false);
      });
  }

  return (
    <div className="page-content">
      <Container fluid className="relative">
        <Loader loading={loader || subjectsLoading} />

        <PageTitle title="Case Studies">
          <div className="page-title-right">
            <Button
              onClick={() => setModalOpen(true)}
              className="btn btn-primary btn-block agora-button border-radius waves-effect waves-light"
            >
              + Add New Case Study
            </Button>
          </div>
        </PageTitle>

        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <AvForm
                  ref={filterForm}
                  onValidSubmit={(e, v) => {
                    handleFilters(e, v);
                  }}
                >
                  <Row className="mb-3">
                    <Col md="2" sm="4">
                      <FormGroup>
                        <Label>Case Study ID:</Label>
                        <AvField
                          name="internalId"
                          placeholder="Search"
                          className="form-control filter-field"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" sm="4">
                      <FormGroup>
                        <Label>Paper Number:</Label>
                        <AvField
                          name="paperNumber"
                          placeholder="Search"
                          className="form-control filter-field"
                          id="paperNumber-filter"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" sm="4">
                      <FormGroup>
                        <Label>Subject:</Label>
                        <AvField type="select" name="subject" className="form-control filter-field">
                          <option value="">Select Subject</option>
                          {subjects.map((subj, idx) => (
                            <option key={idx} value={subj._id}>
                              {subj.title + ' - ' + subj.studyLevelId}
                            </option>
                          ))}
                        </AvField>
                      </FormGroup>
                    </Col>
                    <Col md="2" sm="4">
                      <FormGroup>
                        <Label>Year:</Label>
                        <AvField
                          name="year"
                          placeholder="Search"
                          type="text"
                          className="form-control filter-field"
                          id="year-filter"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4" style={{ marginTop: 30 }}>
                      <Button
                        type="submit"
                        className="btn btn-primary agora-button save-button border-radius waves-effect waves-light"
                      >
                        Filter
                      </Button>
                      <Button
                        type="button"
                        onClick={clearFilters}
                        className="btn btn-primary agora-button cancel-button border-radius waves-effect waves-light ml-2"
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>
                </AvForm>
                <div className="table-responsive">
                  <Table className="table-centered table-nowrap table-hover">
                    <thead className="thead-light font-size-15">
                    <tr>
                      <th scope="col">Case Study ID</th>
                      <th scope="col">Subject</th>
                      <th scope="col">Year</th>
                      <th scope="col">Created Date</th>
                      <th scope="col" className="text-center">
                        Action
                      </th>
                    </tr>
                    </thead>
                    <tbody className="agora-lato">
                    {caseStudies.map((caseStudy, idx) => (
                      <tr key={'_caseStudy_' + idx}>
                        <td>{caseStudy._id}</td>
                        <td>{caseStudy.subject?.title}</td>
                        <td>{caseStudy.year}</td>
                        <td>
                          {new Intl.DateTimeFormat('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit',
                          }).format(new Date(caseStudy.createdAt))}
                        </td>
                        <td className="text-center">
                          <ul className="list-inline font-size-20 contact-links mb-0">
                            <li className="list-inline-item px-2">
                            <Link
                                  to={{
                                    pathname:'case-study',
                                      // question.type.toLowerCase() === 'mcq' ? '/mcq-question' : '/structured-question',
                                    search: '?id=' + caseStudy._id
                                  }}
                                  id={'caseStudy' + caseStudy._id}
                                target="_blank" >

                                <i className="bx bx-show-alt"></i>
                                <UncontrolledTooltip placement="top" target={'caseStudy' + caseStudy._id}>
                                  View
                                </UncontrolledTooltip>
                              </Link>
                            </li>
                            <li className="list-inline-item px-2">
                          <div onClick={()=>handleDeleteCaseStudy(caseStudy)}>
                                <i className="bx bx-trash"></i>
                                <UncontrolledTooltip placement="top" target={'caseStudy' + caseStudy._id}>
                                  Delete
                                </UncontrolledTooltip>
                                </div>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </Table>
                </div>
                <Row>
                  <Col lg="12">
                    <Pagination className="pagination pagination-rounded justify-content-center mt-4">
                      <PaginationItem disabled={pagination.currentPage === 1}>
                        <PaginationLink first onClick={() => paginate(1)} />
                      </PaginationItem>
                      <PaginationItem disabled={pagination.currentPage === 1}>
                        <PaginationLink previous onClick={() => paginate(pagination.currentPage - 1)} />
                      </PaginationItem>
                      {pagination.pages.map((page, index) => (
                        <PaginationItem key={'_page_' + index} active={page === pagination.currentPage}>
                          <PaginationLink onClick={() => paginate(page)}>{page}</PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem disabled={pagination.currentPage === pagination.totalPages}>
                        <PaginationLink next onClick={() => paginate(pagination.currentPage + 1)} />
                      </PaginationItem>
                      <PaginationItem disabled={pagination.currentPage === pagination.totalPages}>
                        <PaginationLink last onClick={() => paginate(pagination.totalPages)} />
                      </PaginationItem>
                    </Pagination>
                    <div className="records-count text-center">
                      <p>
                        Showing <strong>{pagination.startIndex + 1}</strong> to{' '}
                        <strong>{pagination.endIndex + 1}</strong> of <strong>{pagination.totalItems}</strong> records
                      </p>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ModalAddEditCaseStudy show={modalOpen} toggleModal={toggleModal} />
    </div>
  );
};

export default CaseStudies;